import { c as _c } from "react/compiler-runtime";
import { useCallback, useContext, useEffect, useState } from 'react';
import classnames from 'classnames';
import dynamic from 'next/dynamic';
import { LiraAdvContainer } from '@sbt-web/adv';
import { CardBlock } from '@sbt-web/ancillary-services';
import { useViewport } from '@sbt-web/hooks';
import { OptimizelySubitoContext } from '@sbt-web/houston-wrapper';
import { type AdTypeKeyValue, CategoryID, type ItemCategory, type ItemFeature, type ItemGeo, type ItemMap } from '@sbt-web/network/types';
import { Divider, StaticChip } from '@sbt-web/ui';
import { evalBoolFeature, formatMainData, isBrowser } from '@sbt-web/utils';
import { AdsAdDetailsBlock } from '@/client/components/Adv/AdSense/adDetails/AdDetailsBlock';
import { AdsGAMPlacement } from '@/client/components/Adv/GPT';
import { LiraContainerIds } from '@/client/components/Adv/Lira/liraContainerIds';
import { LiraLazySlotBelowGalleryForDesktop } from '@/client/components/Adv/Lira/LiraLazySlotBelowGalleryForDesktop';
import { useLiraLazyOverlayGallery } from '@/client/components/Adv/Lira/useLiraLazyOverlayGallery';
import { RecommendedAds } from '@/client/components/Detail/RecommendedAds';
import flattenFeatures from '@/shared/helpers/FlattenFeatures';
import { prepareRemoteDataAdDetail } from '@/shared/helpers/Hebe';
import { getOrCreatePulse } from '@/tools/tracking/utils';
import { useAdvToggle } from '@/client/hooks/useAdvToggle';
import { useAncillaryServicesStatus } from '@/client/hooks/useAncillaryServicesStatus';
import InternalLinksFooter from '../../SEO/InternalLinks/Footer';
import InternalLinksHeader from '../../SEO/InternalLinks/Header';
import FeatureList, { Feature } from '../FeatureList';
import NavigationRow from '../NavigationRow';
import { LoanProps, provinceCrediteAgricoleList } from '../Skyscraper';
import StickyBar from '../StickyBar';
import type { Props as CommonProps } from './common-props';
import { AdvertiserInfoSection, BottomSection, DescriptionSection, GallerySection, GeneralInfoSection, MainDataSection, RelatedAds, SafetySection, SkyscraperSection, YatmoMapSection } from './Layout';
import * as FeatureConfig from './RealEstateConstants';
import featureSectionStyles from './Layout/style/feature-list-section.module.scss';
import css from './style/container.module.scss';
import gridStyles from './style/grid.module.scss';
const defaultDesktopRightBlockRowSpan = 6;
const CreditAgricoleBlock = dynamic<LoanProps>(() => import('@sbt-web/ancillary-services').then(module => module.CreditAgricoleBlock));
const shouldRenderCreditAgricoleBoxMsite = ({
  geo,
  price,
  type
}: {
  geo: ItemGeo;
  price: ItemFeature;
  type: AdTypeKeyValue;
}): boolean => {
  const priceKey = price?.values?.[0]?.key;
  const cityName = geo?.city?.shortName;
  const typeValue = type.value.toLocaleLowerCase();
  return priceKey && parseInt(priceKey) >= 40000 && parseInt(priceKey) <= 2000000 && cityName && provinceCrediteAgricoleList.includes(cityName) && typeValue === 'in vendita' || false;
};
const checkHasMap = (map?: ItemMap): boolean => map?.longitude != null && map?.latitude != null;
const RealEstateContainer = t0 => {
  const $ = _c(144);
  const {
    ad,
    internalLinks,
    shop,
    shopGoogleReviews,
    favoriteCounter,
    advertiserProfile,
    trustInfo,
    privateUserInfo,
    userAds
  } = t0;
  const {
    isMobile,
    isDesktop
  } = useViewport();
  const [newCreditAgricoleToggle, setNewCreditAgricoleToggle] = useState(false);
  const {
    optimizely
  } = useContext(OptimizelySubitoContext);
  const {
    noAdvToggle,
    optimizelyReady
  } = useAdvToggle();
  const ancillaryServicesEnabled = useAncillaryServicesStatus(ad);
  const noAdvAdvertiser = ad.advertiser.proPremiumDetailEnabled;
  let t1;
  let t2;
  if ($[0] !== optimizely) {
    t1 = () => {
      optimizely?.onReady().then(() => {
        setNewCreditAgricoleToggle(optimizely.isFeatureEnabled("subito.web.adv.credit-agricole"));
      });
    };
    t2 = [optimizely];
    $[0] = optimizely;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== ad) {
    t3 = formatMainData(ad, {
      short: false,
      assetsBase: process.env.NEXT_PUBLIC_ASSETS_BASE_URL
    });
    $[3] = ad;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const mainFeatures = t3;
  const hasMainFeatures = mainFeatures.length > 0;
  let t4;
  if ($[5] !== ad.features) {
    t4 = Object.keys(ad.features);
    $[5] = ad.features;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const hasFeatures = t4.length > 0;
  const mapDetails = ad.geo.map;
  let t5;
  if ($[7] !== mapDetails) {
    t5 = checkHasMap(mapDetails);
    $[7] = mapDetails;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const hasMap = t5;
  const hasSafetyTips = ad.category.id === CategoryID.CaseVacanza;
  const shouldRenderNewCreditAgricoleDesktop = newCreditAgricoleToggle && isDesktop && shouldRenderCreditAgricoleBoxMsite;
  let t6;
  if ($[9] !== hasFeatures || $[10] !== hasMainFeatures || $[11] !== hasMap || $[12] !== hasSafetyTips || $[13] !== shouldRenderNewCreditAgricoleDesktop) {
    t6 = () => {
      let result = defaultDesktopRightBlockRowSpan;
      if (hasMainFeatures) {
        result = defaultDesktopRightBlockRowSpan + 1;
      }
      if (hasFeatures) {
        result = result + 2;
      }
      if (hasMap) {
        result = result + 1;
      }
      if (hasSafetyTips) {
        result = result + 1;
      }
      if (shouldRenderNewCreditAgricoleDesktop) {
        result = result + 2;
      }
      return result;
    };
    $[9] = hasFeatures;
    $[10] = hasMainFeatures;
    $[11] = hasMap;
    $[12] = hasSafetyTips;
    $[13] = shouldRenderNewCreditAgricoleDesktop;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const [desktopRightBlockRowSpan, setDesktopRightBlockRowSpan] = useState(t6);
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setDesktopRightBlockRowSpan(_temp);
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const increaseRowSpan = t7;
  let t8;
  if ($[16] !== ad || $[17] !== isMobile) {
    t8 = {
      ad,
      isMobile
    };
    $[16] = ad;
    $[17] = isMobile;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  useLiraLazyOverlayGallery(t8);
  let t9;
  if ($[19] !== internalLinks) {
    t9 = internalLinks !== null ? <div className={classnames(gridStyles["detail-component"], gridStyles["navigation-detail-container"])}><InternalLinksHeader links={internalLinks.header} /></div> : null;
    $[19] = internalLinks;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = classnames(css["inner-ad-container"], gridStyles["detail-container"]);
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== desktopRightBlockRowSpan) {
    t11 = {
      "--right-block-row-span": desktopRightBlockRowSpan
    };
    $[22] = desktopRightBlockRowSpan;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = classnames([gridStyles["detail-component"], gridStyles["navigation-detail-container"]]);
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== ad) {
    t13 = <section className={t12}><NavigationRow item={ad} /></section>;
    $[25] = ad;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [gridStyles["detail-component"], gridStyles.gallery];
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== ad) {
    t15 = <GallerySection classes={t14} ad={ad} />;
    $[28] = ad;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== isMobile || $[31] !== noAdvAdvertiser || $[32] !== noAdvToggle) {
    t16 = isMobile && noAdvToggle !== "B" && !noAdvAdvertiser && <AdsGAMPlacement classes={[gridStyles["detail-component"]]} id={LiraContainerIds.adView.anchorGallery} fixedHeight={50} />;
    $[30] = isMobile;
    $[31] = noAdvAdvertiser;
    $[32] = noAdvToggle;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== noAdvAdvertiser || $[35] !== noAdvToggle) {
    t17 = noAdvToggle !== "B" && !noAdvAdvertiser && <LiraLazySlotBelowGalleryForDesktop cssClass={gridStyles["detail-component"]} />;
    $[34] = noAdvAdvertiser;
    $[35] = noAdvToggle;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = classnames(gridStyles["detail-component"], gridStyles["right-container"]);
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = [gridStyles["ad-info-container"], gridStyles["ad-info-override"]];
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== ad || $[40] !== advertiserProfile || $[41] !== favoriteCounter || $[42] !== privateUserInfo || $[43] !== shop || $[44] !== shopGoogleReviews || $[45] !== trustInfo || $[46] !== userAds) {
    t20 = <GeneralInfoSection classes={t19} ad={ad} shop={shop} shopGoogleReviews={shopGoogleReviews} favoriteCounter={favoriteCounter} advertiserProfile={advertiserProfile} trustInfo={trustInfo} shippingCosts={null} privateUserInfo={privateUserInfo} userAds={userAds} />;
    $[39] = ad;
    $[40] = advertiserProfile;
    $[41] = favoriteCounter;
    $[42] = privateUserInfo;
    $[43] = shop;
    $[44] = shopGoogleReviews;
    $[45] = trustInfo;
    $[46] = userAds;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== ad || $[49] !== isDesktop || $[50] !== newCreditAgricoleToggle) {
    t21 = isDesktop ? <SkyscraperSection item={ad} classes={[gridStyles.skyscraper]} newCreditAgricoleToggle={newCreditAgricoleToggle} /> : null;
    $[48] = ad;
    $[49] = isDesktop;
    $[50] = newCreditAgricoleToggle;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== t20 || $[53] !== t21) {
    t22 = <div className={t18}>{t20}{t21}</div>;
    $[52] = t20;
    $[53] = t21;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== hasMainFeatures || $[56] !== mainFeatures) {
    t23 = hasMainFeatures ? <MainDataSection classes={[gridStyles["detail-component"]]} features={mainFeatures} /> : null;
    $[55] = hasMainFeatures;
    $[56] = mainFeatures;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = [gridStyles["detail-component"], gridStyles.description];
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== ad) {
    t25 = <DescriptionSection ad={ad} classes={t24} />;
    $[59] = ad;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] !== isDesktop || $[62] !== noAdvAdvertiser || $[63] !== noAdvToggle) {
    t26 = isDesktop || noAdvToggle === "B" || noAdvAdvertiser ? null : <><Divider spacing="null" classes={[gridStyles["detail-component"], gridStyles["section-divider"]]} /><LiraAdvContainer style={{
        minHeight: "250px"
      }} className={gridStyles["detail-component"]} idForAd={LiraContainerIds.adView.boxBelow1} inlineStyleForAd={{
        display: "block"
      }} /></>;
    $[61] = isDesktop;
    $[62] = noAdvAdvertiser;
    $[63] = noAdvToggle;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  let t27;
  if ($[65] !== ad.category || $[66] !== ad.features || $[67] !== ad.type || $[68] !== hasFeatures) {
    t27 = hasFeatures ? <><Divider spacing="null" classes={[gridStyles["detail-component"], gridStyles["section-divider"]]} /><RealEstateFeatureSection adCategory={ad.category} adType={ad.type.key} rawFeatures={ad.features} classes={[gridStyles["detail-component"], gridStyles["feature-list"]]} /></> : null;
    $[65] = ad.category;
    $[66] = ad.features;
    $[67] = ad.type;
    $[68] = hasFeatures;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  let t28;
  if ($[70] !== hasMap || $[71] !== mapDetails) {
    t28 = hasMap ? <><Divider spacing="null" classes={[gridStyles["detail-component"], gridStyles["section-divider"]]} /><YatmoMapSection classes={[gridStyles["detail-component"], gridStyles.map]} map={mapDetails} /></> : null;
    $[70] = hasMap;
    $[71] = mapDetails;
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  let t29;
  if ($[73] !== ad || $[74] !== ancillaryServicesEnabled || $[75] !== shouldRenderNewCreditAgricoleDesktop) {
    t29 = shouldRenderNewCreditAgricoleDesktop && ancillaryServicesEnabled ? <><Divider spacing="null" classes={[gridStyles["detail-component"], gridStyles["section-divider"]]} /><section className={gridStyles["detail-component"]}><CreditAgricoleBlock item={ad} price={ad.features["/price"]} assetsBase={process.env.NEXT_PUBLIC_ASSETS_BASE_URL} pulseInstance={getOrCreatePulse()} /></section></> : null;
    $[73] = ad;
    $[74] = ancillaryServicesEnabled;
    $[75] = shouldRenderNewCreditAgricoleDesktop;
    $[76] = t29;
  } else {
    t29 = $[76];
  }
  let t30;
  if ($[77] !== ad || $[78] !== ancillaryServicesEnabled || $[79] !== isMobile || $[80] !== noAdvToggle || $[81] !== optimizelyReady) {
    t30 = optimizelyReady && noAdvToggle !== "B" && ancillaryServicesEnabled && <CardBlock padding={true} pulseInstance={getOrCreatePulse()} background={true} bannerButtonDesign="outline" item={ad} remoteData={prepareRemoteDataAdDetail("BELOW_FEATURES", ad, isMobile)} />;
    $[77] = ad;
    $[78] = ancillaryServicesEnabled;
    $[79] = isMobile;
    $[80] = noAdvToggle;
    $[81] = optimizelyReady;
    $[82] = t30;
  } else {
    t30 = $[82];
  }
  let t31;
  if ($[83] !== t30) {
    t31 = <div className={gridStyles["detail-component"]}>{t30}</div>;
    $[83] = t30;
    $[84] = t31;
  } else {
    t31 = $[84];
  }
  let t32;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <Divider spacing="null" classes={[gridStyles["detail-component"], gridStyles["section-divider"]]} />;
    $[85] = t32;
  } else {
    t32 = $[85];
  }
  let t33;
  if ($[86] !== isDesktop || $[87] !== noAdvAdvertiser || $[88] !== noAdvToggle) {
    t33 = !isDesktop && noAdvToggle !== "B" && !noAdvAdvertiser && <LiraAdvContainer style={{
      height: "250px"
    }} className={gridStyles["detail-component"]} idForAd={LiraContainerIds.adView.boxBelow3} />;
    $[86] = isDesktop;
    $[87] = noAdvAdvertiser;
    $[88] = noAdvToggle;
    $[89] = t33;
  } else {
    t33 = $[89];
  }
  let t34;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = [gridStyles["detail-component"]];
    $[90] = t34;
  } else {
    t34 = $[90];
  }
  let t35;
  if ($[91] !== ad || $[92] !== advertiserProfile || $[93] !== shop || $[94] !== shopGoogleReviews || $[95] !== trustInfo) {
    t35 = <AdvertiserInfoSection classes={t34} item={ad} shop={shop} shopGoogleReviews={shopGoogleReviews} trustInfo={trustInfo} advertiserProfile={advertiserProfile} />;
    $[91] = ad;
    $[92] = advertiserProfile;
    $[93] = shop;
    $[94] = shopGoogleReviews;
    $[95] = trustInfo;
    $[96] = t35;
  } else {
    t35 = $[96];
  }
  let t36;
  if ($[97] !== ad.category.id || $[98] !== ad.urn || $[99] !== hasSafetyTips) {
    t36 = hasSafetyTips ? <SafetySection classes={[gridStyles["detail-component"]]} category={ad.category.id} urn={ad.urn} /> : null;
    $[97] = ad.category.id;
    $[98] = ad.urn;
    $[99] = hasSafetyTips;
    $[100] = t36;
  } else {
    t36 = $[100];
  }
  let t37;
  let t38;
  if ($[101] !== ad) {
    t37 = <RecommendedAds item={ad} className={gridStyles["detail-component"]} onActualRender={increaseRowSpan} />;
    t38 = <RelatedAds item={ad} className={gridStyles["detail-component"]} onActualRender={increaseRowSpan} />;
    $[101] = ad;
    $[102] = t37;
    $[103] = t38;
  } else {
    t37 = $[102];
    t38 = $[103];
  }
  let t39;
  if ($[104] !== t11 || $[105] !== t13 || $[106] !== t15 || $[107] !== t16 || $[108] !== t17 || $[109] !== t22 || $[110] !== t23 || $[111] !== t25 || $[112] !== t26 || $[113] !== t27 || $[114] !== t28 || $[115] !== t29 || $[116] !== t31 || $[117] !== t33 || $[118] !== t35 || $[119] !== t36 || $[120] !== t37 || $[121] !== t38) {
    t39 = <div className={t10} style={t11}>{t13}{t15}{t16}{t17}{t22}{t23}{t25}{t26}{t27}{t28}{t29}{t31}{t32}{t33}{t35}{t36}{t37}{t38}</div>;
    $[104] = t11;
    $[105] = t13;
    $[106] = t15;
    $[107] = t16;
    $[108] = t17;
    $[109] = t22;
    $[110] = t23;
    $[111] = t25;
    $[112] = t26;
    $[113] = t27;
    $[114] = t28;
    $[115] = t29;
    $[116] = t31;
    $[117] = t33;
    $[118] = t35;
    $[119] = t36;
    $[120] = t37;
    $[121] = t38;
    $[122] = t39;
  } else {
    t39 = $[122];
  }
  let t40;
  if ($[123] !== ad || $[124] !== isMobile) {
    t40 = !isMobile ? <><AdsAdDetailsBlock className={gridStyles["detail-component"]} /><StickyBar item={ad} /></> : null;
    $[123] = ad;
    $[124] = isMobile;
    $[125] = t40;
  } else {
    t40 = $[125];
  }
  let t41;
  if ($[126] !== isDesktop) {
    t41 = isDesktop ? null : <AdsGAMPlacement id={LiraContainerIds.adView.boxBelow2} />;
    $[126] = isDesktop;
    $[127] = t41;
  } else {
    t41 = $[127];
  }
  let t42;
  if ($[128] !== t39 || $[129] !== t40 || $[130] !== t41) {
    t42 = <div className={css["outer-ad-container"]}>{t39}{t40}{t41}</div>;
    $[128] = t39;
    $[129] = t40;
    $[130] = t41;
    $[131] = t42;
  } else {
    t42 = $[131];
  }
  let t43;
  if ($[132] !== ad) {
    t43 = <BottomSection item={ad} />;
    $[132] = ad;
    $[133] = t43;
  } else {
    t43 = $[133];
  }
  let t44;
  if ($[134] !== internalLinks) {
    t44 = internalLinks !== null ? <InternalLinksFooter links={internalLinks.footer} /> : null;
    $[134] = internalLinks;
    $[135] = t44;
  } else {
    t44 = $[135];
  }
  let t45;
  if ($[136] !== isDesktop) {
    t45 = isDesktop ? <div id="apn_body" /> : null;
    $[136] = isDesktop;
    $[137] = t45;
  } else {
    t45 = $[137];
  }
  let t46;
  if ($[138] !== t42 || $[139] !== t43 || $[140] !== t44 || $[141] !== t45 || $[142] !== t9) {
    t46 = <>{t9}{t42}{t43}{t44}{t45}</>;
    $[138] = t42;
    $[139] = t43;
    $[140] = t44;
    $[141] = t45;
    $[142] = t9;
    $[143] = t46;
  } else {
    t46 = $[143];
  }
  return t46;
};
type FeatureCollection = {
  [uri: string]: ItemFeature;
};
interface FeatureSectionProps {
  adCategory: ItemCategory;
  adType: string;
  rawFeatures: FeatureCollection;
  classes?: string[];
}
const fillCoreFeatures = (adFeatures: FeatureCollection, adCategory: ItemCategory, adType: string): Feature[] => {
  const categoryTypeKey = `${adCategory.id}_${adType}`;
  const categoryFeatures = FeatureConfig.coreFeaturesByCategoryAndType.get(categoryTypeKey) ?? [];
  const filledFeatures: Feature[] = categoryFeatures.filter(uri => {
    const notInAdFeatures = !(uri in adFeatures);

    // If we don't have a label we can't make a placeholder.
    const labelExists = FeatureConfig.coreFeatureLabels.has(uri);
    return notInAdFeatures && labelExists;
  }).map(f => {
    return {
      label: FeatureConfig.coreFeatureLabels.get(f),
      value: '-'
    };
  });
  const flatFeatures = flattenFeatures(adFeatures);
  return flatFeatures.concat(filledFeatures);
};
const fillEnergyFeatures = (adFeatures: FeatureCollection): Feature[] => {
  if (Object.keys(adFeatures).length === 0) {
    return [];
  }
  const energyFeatureURIs = FeatureConfig.energyFeatureLabels.keys();
  const filledFeatures: Feature[] = [];
  for (const featureUri of Array.from(energyFeatureURIs)) {
    const notInAdFeatures = !(featureUri in adFeatures);
    if (notInAdFeatures) {
      filledFeatures.push({
        label: FeatureConfig.energyFeatureLabels.get(featureUri),
        value: '-'
      });
    }
  }
  const flatFeatures = flattenFeatures(adFeatures);
  return flatFeatures.concat(filledFeatures);
};
const sortFeatures = (config: Map<string, number>, left: Feature, right: Feature): number => {
  const leftIndex = left.label && config.get(left.label);
  const rightIndex = right.label && config.get(right.label);
  if (leftIndex && rightIndex) {
    return leftIndex - rightIndex;
  } else {
    // This should never happen but it is a failsafe.
    return 0;
  }
};
const sortCoreFeatures = sortFeatures.bind(null, FeatureConfig.coreFeatureSortOrder);
const sortDetailFeatures = sortFeatures.bind(null, FeatureConfig.detailFeatureSortOrder);
const sortEnergyFeatures = sortFeatures.bind(null, FeatureConfig.energyFeatureSortOrder);
function RealEstateFeatureSection(props) {
  const $ = _c(30);
  const [isGapSupported, setIsGapSupported] = useState(true);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (!isBrowser()) {
        setIsGapSupported(true);
      } else {
        const flex = document.createElement("div");
        flex.style.display = "flex";
        flex.style.flexDirection = "column";
        flex.style.rowGap = "1px";
        flex.appendChild(document.createElement("div"));
        flex.appendChild(document.createElement("div"));
        document.body.appendChild(flex);
        const isSupported = flex.scrollHeight === 1;
        flex.parentNode?.removeChild(flex);
        setIsGapSupported(isSupported);
      }
    };
    t1 = [setIsGapSupported];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  const {
    rawFeatures,
    classes: t2,
    adCategory,
    adType
  } = props;
  let t3;
  if ($[2] !== t2) {
    t3 = t2 === undefined ? [] : t2;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const classes = t3;
  let energyDisplayFeatures;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[4] !== adCategory || $[5] !== adType || $[6] !== classes || $[7] !== isGapSupported || $[8] !== rawFeatures) {
    t9 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const detailFeatures = {};
      const energyFeatures = {};
      const remainingFeatures = {};
      for (const uri in rawFeatures) {
        if (FeatureConfig.detailFeatureLabels.has(uri)) {
          const detailFeature = rawFeatures[uri];
          if (evalBoolFeature(detailFeature)) {
            detailFeatures[uri] = detailFeature;
          } else {
            continue;
          }
        } else {
          if (FeatureConfig.energyFeatureLabels.has(uri)) {
            energyFeatures[uri] = rawFeatures[uri];
          } else {
            remainingFeatures[uri] = rawFeatures[uri];
          }
        }
      }
      const coreFeatures = fillCoreFeatures(remainingFeatures, adCategory, adType);
      const coreDisplayFeatures = coreFeatures.sort(sortCoreFeatures);
      const detailDisplayFeatures = flattenFeatures(detailFeatures).sort(sortDetailFeatures);
      const filledEnergyFeatures = fillEnergyFeatures(energyFeatures);
      energyDisplayFeatures = filledEnergyFeatures.sort(sortEnergyFeatures);
      if (coreDisplayFeatures.length === 0 && detailDisplayFeatures.length === 0 && energyDisplayFeatures.length === 0) {
        t9 = null;
        break bb0;
      }
      t4 = "feature-section";
      if ($[16] !== classes) {
        t5 = classnames(...classes, gridStyles["detail-component"]);
        $[16] = classes;
        $[17] = t5;
      } else {
        t5 = $[17];
      }
      if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <h2 className={classnames("headline-6", featureSectionStyles["section-title"])}>Caratteristiche</h2>;
        $[18] = t6;
      } else {
        t6 = $[18];
      }
      if ($[19] !== coreDisplayFeatures) {
        t7 = <FeatureList features={coreDisplayFeatures} />;
        $[19] = coreDisplayFeatures;
        $[20] = t7;
      } else {
        t7 = $[20];
      }
      t8 = detailDisplayFeatures.length > 0 ? <><p className={classnames("subheading", featureSectionStyles["section-subtitle"])}>Dettagli</p><div id={featureSectionStyles["detail-chip-container"]} className={classnames({
          [featureSectionStyles["gap-fallback"]]: !isGapSupported
        })}>{detailDisplayFeatures.map(_temp2)}</div></> : null;
    }
    $[4] = adCategory;
    $[5] = adType;
    $[6] = classes;
    $[7] = isGapSupported;
    $[8] = rawFeatures;
    $[9] = energyDisplayFeatures;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    energyDisplayFeatures = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
    t9 = $[15];
  }
  if (t9 !== Symbol.for("react.early_return_sentinel")) {
    return t9;
  }
  let t10;
  if ($[21] !== energyDisplayFeatures) {
    t10 = energyDisplayFeatures.length > 0 ? <><p className={classnames("subheading", featureSectionStyles["section-subtitle"])}>Energia e riscaldamento</p><FeatureList features={energyDisplayFeatures} singleColumn={true} /></> : null;
    $[21] = energyDisplayFeatures;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t4 || $[25] !== t5 || $[26] !== t6 || $[27] !== t7 || $[28] !== t8) {
    t11 = <section id={t4} className={t5}>{t6}{t7}{t8}{t10}</section>;
    $[23] = t10;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
}
function _temp2(f) {
  return <StaticChip key={f.label} size="medium">{f.label}</StaticChip>;
}
export default RealEstateContainer;

// Exported for testing
export { RealEstateFeatureSection, RealEstateContainer };
function _temp(span) {
  return span + 1;
}